import { isSubagentDefunctOutcome } from "../../../../src/agents/subagent-defunct.js";
import { parseAgentSessionKey } from "../../../../src/routing/session-key.js";
import { orchestrationIdFromPlanUnitLabel } from "../chat/fleet-completion-notice.ts";
import { isSubagentRunCancellable } from "../chat/subagent-run-status.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type { GatewaySessionRow, SessionsListResult } from "../types.ts";
import type { AgentRunEventSummary, ChatSubagentCard, ChatSubagentStatus } from "../ui-types.ts";
import { generateUUID } from "../uuid.ts";

export interface SubagentRegistryRun {
  runId: string;
  childSessionKey: string;
  requesterSessionKey: string;
  task: string;
  label?: string;
  createdAt: number;
  startedAt?: number;
  endedAt?: number;
  outcome?: { status: string; error?: string };
  /** Session-file ids handed to this child. Listed in the UI header. */
  sessionFileIds?: string[];
}

/**
 * True when the session row's last run reached a terminal failure state.
 *
 * `sessions.list` reports this as `abortedLastRun` (operator cancel) and
 * `lastRunError` (crashed/errored run); there is no `status` field on the
 * row.
 */
function isTerminalRowStatus(row: GatewaySessionRow): boolean {
  return row.abortedLastRun === true || typeof row.lastRunError === "string";
}

/** Map a terminal session row onto the card status. */
function rowStatusOf(row: GatewaySessionRow): ChatSubagentStatus {
  return row.abortedLastRun === true ? "cancelled" : "error";
}

function statusFromRegistryOutcome(outcome?: {
  status: string;
  error?: string;
}): ChatSubagentStatus {
  if (!outcome) {
    return "error";
  }
  if (
    isSubagentDefunctOutcome(outcome) ||
    outcome.status === "error" ||
    outcome.status === "timeout" ||
    outcome.status === "stalled"
  ) {
    return "error";
  }
  if (outcome.status === "ok") {
    return "done";
  }
  return "error";
}

export async function loadSubagentRegistryRuns(
  client: GatewayBrowserClient,
  requesterSessionKey: string,
): Promise<SubagentRegistryRun[]> {
  try {
    const res = await client.request<{ runs?: SubagentRegistryRun[] }>("subagents.list", {
      requesterSessionKey,
    });
    return Array.isArray(res?.runs) ? res.runs : [];
  } catch {
    return [];
  }
}

/**
 * Procset-derived status for one subagent child. Returned by the
 * `procset.status` gateway RPC; the Inspector overlays it onto cards
 * so the operator sees live `ps + log-mtime` truth instead of stale
 * registry rows. Schema mirrors `ChildStatus` from
 * `src/employees/procset/dispatch.ts`.
 */
export interface ProcsetChildStatus {
  state: "running" | "exited" | "killed" | "unknown";
  exitCode?: number;
  ageMs: number;
  cpuPct?: number;
  logPath: string;
  label: string;
}

/**
 * Fetch procset-derived status for a batch of registry child sessionKeys.
 * Best-effort: any error returns an empty map so the Inspector falls back
 * to registry-only truth rather than blowing up the render.
 */
export async function loadProcsetStatuses(
  client: GatewayBrowserClient,
  childSessionKeys: string[],
): Promise<Record<string, ProcsetChildStatus | null>> {
  if (childSessionKeys.length === 0) return {};
  try {
    const res = await client.request<{ statuses?: Record<string, ProcsetChildStatus | null> }>(
      "procset.status",
      { childSessionKeys },
    );
    return res?.statuses ?? {};
  } catch {
    return {};
  }
}

function procsetStateToCardStatus(state: ProcsetChildStatus["state"]): ChatSubagentStatus | null {
  if (state === "running") return "running";
  if (state === "exited") return "done";
  if (state === "killed") return "cancelled";
  return null; // "unknown" → leave card status alone
}

const TERMINAL_SUBAGENT_STATUSES = new Set<ChatSubagentStatus>(["done", "error", "cancelled"]);

/**
 * Overlay live procset truth onto subagent cards. For each card whose
 * `childSessionKey` has a procset entry, replace `status` with the
 * procset-derived state and stamp `updatedAt` so the collapsed-card age
 * reflects the truth. Cards without a procset binding are returned
 * unchanged (registry-only).
 */
export function overlayProcsetStatusOnCards(
  cards: ChatSubagentCard[],
  statuses: Record<string, ProcsetChildStatus | null>,
  runs: SubagentRegistryRun[] = [],
): ChatSubagentCard[] {
  if (Object.keys(statuses).length === 0) return cards;
  const endedChildKeys = new Set(
    runs.filter((run) => run.endedAt).map((run) => run.childSessionKey),
  );
  return cards.map((card) => {
    const childKey = card.childSessionKey?.trim();
    if (!childKey) return card;
    if (endedChildKeys.has(childKey) || TERMINAL_SUBAGENT_STATUSES.has(card.status)) {
      return card;
    }
    const overlay = statuses[childKey];
    if (!overlay) return card; // null means no procset binding
    const mapped = procsetStateToCardStatus(overlay.state);
    if (!mapped) return card; // "unknown" → keep registry status
    return {
      ...card,
      status: mapped,
      updatedAt: Date.now(),
    };
  });
}

const STALE_REGISTRY_ERROR = "Subagent run no longer in registry";

/**
 * Terminalize in-flight cards whose child session is absent from the registry.
 * Runs with `cleanup: "delete"` are removed after completion; without this
 * pass the composer pill and inspector disagree (running vs idle/defunct).
 */
export function reconcileStaleSubagentCards(
  cards: ChatSubagentCard[],
  runs: SubagentRegistryRun[],
): ChatSubagentCard[] {
  const byChildKey = new Map(runs.map((run) => [run.childSessionKey, run]));
  return cards.map((card) => {
    if (TERMINAL_SUBAGENT_STATUSES.has(card.status)) {
      return card;
    }
    const childKey = card.childSessionKey?.trim();
    if (!childKey) {
      return card;
    }
    const run = byChildKey.get(childKey);
    if (run && !run.endedAt) {
      return card;
    }
    if (run?.endedAt) {
      return {
        ...card,
        runId: run.runId || card.runId,
        status: statusFromRegistryOutcome(run.outcome),
        error: run.outcome?.error ?? card.error,
        updatedAt: Date.now(),
      };
    }
    return {
      ...card,
      status: "error",
      error: card.error ?? STALE_REGISTRY_ERROR,
      updatedAt: Date.now(),
    };
  });
}

export function applyRegistryRunsToCards(
  cards: ChatSubagentCard[],
  runs: SubagentRegistryRun[],
): ChatSubagentCard[] {
  const byChildKey = new Map(runs.map((run) => [run.childSessionKey, run]));
  const mapped = cards.map((card) => {
    const run = card.childSessionKey ? byChildKey.get(card.childSessionKey) : undefined;
    if (!run) {
      return card;
    }
    const status = run.endedAt ? statusFromRegistryOutcome(run.outcome) : card.status;
    const label = run.label ?? card.label;
    return {
      ...card,
      runId: run.runId || card.runId,
      task: run.task || card.task,
      label,
      orchestrationId: card.orchestrationId ?? orchestrationIdFromPlanUnitLabel(label),
      status,
      error: run.outcome?.error ?? card.error,
      // Same ids the child's preparation node resolved.
      ...(run.sessionFileIds && run.sessionFileIds.length > 0
        ? { sessionFileIds: [...run.sessionFileIds] }
        : {}),
    };
  });
  return reconcileStaleSubagentCards(mapped, runs);
}

export function syncCardsFromRegistry(
  sessionKey: string,
  cards: ChatSubagentCard[],
  runs: SubagentRegistryRun[],
): ChatSubagentCard[] {
  const withRegistry = applyRegistryRunsToCards(cards, runs);
  const knownKeys = new Set(withRegistry.map((c) => c.childSessionKey).filter(Boolean));
  const extras: ChatSubagentCard[] = [];
  for (const run of runs) {
    if (knownKeys.has(run.childSessionKey)) {
      continue;
    }
    if (run.requesterSessionKey !== sessionKey) {
      continue;
    }
    const parsed = parseAgentSessionKey(run.childSessionKey);
    const label = run.label ?? parsed?.agentId ?? "Subagent";
    extras.push({
      id: `registry-${run.runId}`,
      runId: run.runId,
      childSessionKey: run.childSessionKey,
      // The registry is the durable record of who spawned this child. Without
      // it the card renders as a root and the Inspector tree collapses to a
      // flat peer list after any reload — the hierarchy silently disappears
      // exactly when the operator navigates back to check on a fan-out.
      parentSessionKey: run.requesterSessionKey,
      label,
      orchestrationId: orchestrationIdFromPlanUnitLabel(label),
      agentId: parsed?.agentId ?? "main",
      task: run.task,
      status: run.endedAt ? statusFromRegistryOutcome(run.outcome) : "accepted",
      error: run.outcome?.error,
      createdAt: run.createdAt,
      ...(run.sessionFileIds && run.sessionFileIds.length > 0
        ? { sessionFileIds: [...run.sessionFileIds] }
        : {}),
    });
  }
  return extras.length ? [...withRegistry, ...extras] : withRegistry;
}

/** Upsert a parallel subagent roster card from a parent-session `chat.run` event. */
export function upsertSubagentCardFromChatRun(
  cards: ChatSubagentCard[],
  payload: {
    runId: string;
    childSessionKey: string;
    label?: string;
  },
): ChatSubagentCard[] {
  const childSessionKey = payload.childSessionKey.trim();
  const runId = payload.runId.trim();
  if (!childSessionKey || !runId) {
    return cards;
  }
  const now = Date.now();
  const parsed = parseAgentSessionKey(childSessionKey);
  const agentId = parsed?.agentId ?? "main";
  const label = payload.label?.trim() || parsed?.agentId || "Subagent";
  const orchestrationId = orchestrationIdFromPlanUnitLabel(label);
  const existing = cards.find((c) => c.childSessionKey === childSessionKey || c.runId === runId);
  if (existing) {
    return cards.map((c) => {
      if (c.childSessionKey !== childSessionKey && c.runId !== runId) {
        return c;
      }
      const revive =
        TERMINAL_SUBAGENT_STATUSES.has(c.status) && c.runId !== runId ? "running" : c.status;
      return {
        ...c,
        runId,
        childSessionKey,
        label: payload.label?.trim() || c.label,
        orchestrationId: orchestrationId ?? c.orchestrationId,
        agentId: c.agentId || agentId,
        status: revive,
        updatedAt: now,
      };
    });
  }
  return [
    ...cards,
    {
      id: `chat-run-${runId}`,
      runId,
      childSessionKey,
      label,
      orchestrationId,
      agentId,
      task: "",
      status: "accepted",
      createdAt: now,
      updatedAt: now,
    },
  ];
}

export function addRunEvents(
  cards: ChatSubagentCard[],
  runId: string,
  events: AgentRunEventSummary[],
): ChatSubagentCard[] {
  return cards.map((c) => (c.runId === runId ? { ...c, events } : c));
}

export type SpawnSubagentUiOpts = {
  sessionKey: string;
  task: string;
  label?: string;
  agentId?: string;
  model?: string;
  /** Operator UI fan-out — bypass unified-planner direct-spawn guard on main. */
  spawnSource?: "planner" | "direct";
};

export async function requestSessionsSpawn(
  client: GatewayBrowserClient,
  opts: SpawnSubagentUiOpts,
): Promise<
  | { ok: true; childSessionKey: string; runId: string; model?: string; warning?: string }
  | { ok: false; error: string; cap?: boolean }
> {
  const sessionKey = opts.sessionKey.trim();
  if (!sessionKey) {
    return { ok: false, error: "sessionKey required for sessions.spawn" };
  }
  try {
    const res = await client.request<{
      status?: string;
      childSessionKey?: string;
      runId?: string;
      model?: string;
      warning?: string;
    }>("sessions.spawn", {
      sessionKey,
      task: opts.task,
      label: opts.label,
      agentId: opts.agentId,
      model: opts.model,
      spawnSource: opts.spawnSource,
    });
    const childSessionKey = typeof res?.childSessionKey === "string" ? res.childSessionKey : "";
    const runId = typeof res?.runId === "string" ? res.runId : "";
    if (!childSessionKey || !runId) {
      return { ok: false, error: "invalid spawn response" };
    }
    return {
      ok: true,
      childSessionKey,
      runId,
      model: typeof res.model === "string" ? res.model : undefined,
      warning: typeof res.warning === "string" ? res.warning : undefined,
    };
  } catch (err) {
    const msg = String(err);
    const cap =
      msg.includes("concurrency limit") ||
      msg.includes("UNAVAILABLE") ||
      /limit reached/i.test(msg);
    return { ok: false, error: msg, cap };
  }
}

/** Merge spawn result into an existing card by id. */
export function applySpawnResultToCard(
  cards: ChatSubagentCard[],
  id: string,
  result:
    | { ok: true; childSessionKey: string; runId: string; model?: string; warning?: string }
    | { ok: false; error: string; cap?: boolean },
): ChatSubagentCard[] {
  return cards.map((c) => {
    if (c.id !== id) {
      return c;
    }
    if (!result.ok) {
      return {
        ...c,
        status: result.cap ? "queued" : "error",
        error: result.error,
      };
    }
    return {
      ...c,
      status: "accepted",
      runId: result.runId,
      childSessionKey: result.childSessionKey,
      model: result.model ?? c.model,
      warning: result.warning,
    };
  });
}

export function upsertSubagentToolHint(
  cards: ChatSubagentCard[],
  runId: string,
  hint: string,
  phase?: string,
): ChatSubagentCard[] {
  return cards.map((c) =>
    c.runId === runId
      ? { ...c, toolHint: hint.slice(0, 4000), phase: phase ?? c.phase, updatedAt: Date.now() }
      : c,
  );
}

export function markSubagentLifecycle(
  cards: ChatSubagentCard[],
  runId: string,
  phase: string,
  err?: string,
): ChatSubagentCard[] {
  return cards.map((c) => {
    if (c.runId !== runId) {
      return c;
    }
    if (phase === "error") {
      return {
        ...c,
        status: "error" as const,
        error: err ?? "subagent failed",
        phase,
        updatedAt: Date.now(),
      };
    }
    if (phase === "end") {
      return { ...c, status: "done" as const, phase, updatedAt: Date.now() };
    }
    if (phase === "initializing") {
      return { ...c, status: "cold-start" as const, phase, updatedAt: Date.now() };
    }
    return { ...c, phase, updatedAt: Date.now() };
  });
}

/**
 * Hard ceiling on a subagent card's retained `streamText`.
 *
 * `outputPreview` was already capped at 500 chars, but `streamText` was not —
 * so a long-running research subagent accumulated its entire output in every
 * card that referenced it, in the live list, in the per-session stash, and in
 * the IndexedDB mirror. Rendering only ever showed the tail, so keeping the
 * whole string bought nothing and cost megabytes.
 *
 * 20 000 chars is far more than any card renders (the card slices to 4 000)
 * and is small enough that a few hundred cards stay cheap.
 */
export const MAX_SUBAGENT_STREAM_TEXT_CHARS = 20_000;

/** Keep the tail — the part every renderer actually shows. */
export function capSubagentStreamText(text: string): string {
  if (text.length <= MAX_SUBAGENT_STREAM_TEXT_CHARS) {
    return text;
  }
  return text.slice(-MAX_SUBAGENT_STREAM_TEXT_CHARS);
}

/** Promote pre-stream cards to running once output begins streaming. */
export function applySubagentStreamDelta(card: ChatSubagentCard, merged: string): ChatSubagentCard {
  const hadStream = Boolean(card.streamText?.trim());
  const hasStream = Boolean(merged.trim());
  const preStream =
    card.status === "accepted" ||
    card.status === "cold-start" ||
    card.status === "spawning" ||
    card.status === "queued";
  const capped = capSubagentStreamText(merged);
  return {
    ...card,
    streamText: capped,
    outputPreview: capped.slice(-500),
    updatedAt: Date.now(),
    status: !hadStream && hasStream && preStream ? "running" : card.status,
  };
}

export function mergeChildChatDelta(
  cards: ChatSubagentCard[],
  childSessionKey: string,
  text: string,
): ChatSubagentCard[] {
  const capped = capSubagentStreamText(text);
  return cards.map((c) =>
    c.childSessionKey === childSessionKey
      ? { ...c, streamText: capped, outputPreview: capped.slice(-500), updatedAt: Date.now() }
      : c,
  );
}

/**
 * After reconnect: rebuild strip from sessions spawned by the current session (best-effort).
 */
export function mergeRestoredSubagents(
  sessionKey: string,
  list: SessionsListResult | null,
  existing: ChatSubagentCard[],
  registryRuns?: SubagentRegistryRun[],
): ChatSubagentCard[] {
  const restored = cardsFromSpawnedSessions(sessionKey, list, registryRuns);
  const known = new Set(existing.map((c) => c.childSessionKey).filter((k) => k.length > 0));
  const extra = restored.filter((r) => r.childSessionKey && !known.has(r.childSessionKey));
  const merged = extra.length ? [...existing, ...extra] : existing;
  return registryRuns ? syncCardsFromRegistry(sessionKey, merged, registryRuns) : merged;
}

export function cardsFromSpawnedSessions(
  sessionKey: string,
  list: SessionsListResult | null,
  registryRuns?: SubagentRegistryRun[],
): ChatSubagentCard[] {
  const registryByKey = new Map((registryRuns ?? []).map((run) => [run.childSessionKey, run]));
  const sessions = list?.sessions ?? [];
  const out: ChatSubagentCard[] = [];
  for (const row of sessions) {
    const key = row.key ?? "";
    if (!key.includes(":subagent:")) {
      continue;
    }
    const spawnedBy = row.spawnedBy;
    if (spawnedBy !== sessionKey) {
      continue;
    }
    const parsed = parseAgentSessionKey(key);
    const registry = registryByKey.get(key);
    const consultedRegistry = registryRuns !== undefined;
    let status: ChatSubagentStatus;
    if (registry?.endedAt) {
      status = statusFromRegistryOutcome(registry.outcome);
    } else if (registry) {
      status = "accepted";
    } else if (consultedRegistry) {
      // No live registry entry: trust the session row. The gateway marks a
      // failed/cancelled last run via `lastRunError` / `abortedLastRun`,
      // not a `status` field.
      status = isTerminalRowStatus(row) ? rowStatusOf(row) : "error";
    } else if (isTerminalRowStatus(row)) {
      status = rowStatusOf(row);
    } else {
      status = "running";
    }
    out.push({
      id: registry ? `registry-${registry.runId}` : `restore-${key}`,
      runId: registry?.runId ?? "",
      childSessionKey: key,
      // `spawnedBy` is the durable owner edge; without it the restored card
      // loses its place in the Inspector tree on reconnect.
      parentSessionKey: spawnedBy,
      label: registry?.label ?? row.label ?? row.displayName ?? "Subagent",
      agentId: parsed?.agentId ?? "main",
      task: registry?.task ?? row.lastMessagePreview ?? "",
      status,
      error:
        registry?.outcome?.error ??
        (consultedRegistry && !registry ? STALE_REGISTRY_ERROR : undefined),
      createdAt: row.updatedAt ?? registry?.createdAt ?? Date.now(),
    });
  }
  return out;
}

/**
 * JAISIU-3319 / Pillar H: ask the gateway to cancel an in-flight subagent run
 * by its child session key. Returns `{ ok: true, cancelled, broadcast }` when
 * the RPC succeeded; `{ ok: false, error }` on transport / validation errors.
 * `cancelled=false` with `hadRun=false` is normal when the run has already
 * settled — cancellation is idempotent and must not throw.
 */
export async function requestSessionsCancel(
  client: GatewayBrowserClient,
  opts: { childSessionKey: string; reason?: string; broadcast?: boolean },
): Promise<
  | {
      ok: true;
      cancelled: boolean;
      broadcast: boolean;
      hadRun: boolean;
      reason: string;
      runId: string | null;
    }
  | { ok: false; error: string }
> {
  const childSessionKey = opts.childSessionKey.trim();
  if (!childSessionKey) {
    return { ok: false, error: "childSessionKey required for sessions.cancel" };
  }
  try {
    const res = await client.request<{
      cancelled?: boolean;
      broadcast?: boolean;
      hadRun?: boolean;
      reason?: string;
      runId?: string | null;
    }>("sessions.cancel", {
      childSessionKey,
      reason: opts.reason,
      broadcast: opts.broadcast !== false,
    });
    return {
      ok: true,
      cancelled: Boolean(res?.cancelled),
      broadcast: Boolean(res?.broadcast),
      hadRun: Boolean(res?.hadRun),
      reason: typeof res?.reason === "string" ? res.reason : "cancelled",
      runId: typeof res?.runId === "string" ? res.runId : null,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export function createPendingSubagentCard(
  task: string,
  agentId: string,
  label?: string,
): ChatSubagentCard {
  const createdAt = Date.now();
  return {
    id: generateUUID(),
    runId: "",
    childSessionKey: "",
    label: label ?? agentId,
    agentId,
    task,
    status: "spawning",
    createdAt,
  };
}

/** Child session keys that are still cancellable (i.e. worth sending to the RPC). */
export function cancellableChildSessionKeys(cards: ChatSubagentCard[]): string[] {
  return cards
    .filter((c) => c.childSessionKey.trim().length > 0 && isSubagentRunCancellable(c.status))
    .map((c) => c.childSessionKey);
}

/**
 * Ask the gateway to terminate every in-flight child of `parentSessionKey`
 * ("Stop this orchestration").
 *
 * One RPC rather than N `sessions.cancel` calls: the handler finalizes each
 * registry record, aborts the embedded runtime, and sends a single aggregate
 * broadcast to the parent, so the thread reflects the stop once instead of
 * five times. `childSessionKeys` narrows the stop to the fan-out on screen
 * when a parent has run several sequentially.
 *
 * Non-throwing by contract — cancellation is idempotent, and a partial
 * failure still has to update the aggregate status in the UI.
 */
export async function requestCancelAllSubagents(
  client: GatewayBrowserClient,
  opts: { parentSessionKey: string; childSessionKeys?: string[]; reason?: string },
): Promise<
  | {
      ok: true;
      cancelled: number;
      skipped: number;
      cancelledChildSessionKeys: string[];
      reason: string;
    }
  | { ok: false; error: string }
> {
  const parentSessionKey = opts.parentSessionKey.trim();
  if (!parentSessionKey) {
    return { ok: false, error: "parentSessionKey required for subagents.cancelAll" };
  }
  try {
    const res = await client.request<{
      cancelled?: number;
      skipped?: number;
      cancelledChildSessionKeys?: string[];
      reason?: string;
    }>("subagents.cancelAll", {
      requesterSessionKey: parentSessionKey,
      childSessionKeys: opts.childSessionKeys,
      reason: opts.reason,
    });
    return {
      ok: true,
      cancelled: Number(res?.cancelled ?? 0),
      skipped: Number(res?.skipped ?? 0),
      cancelledChildSessionKeys: res?.cancelledChildSessionKeys ?? [],
      reason: typeof res?.reason === "string" ? res.reason : "cancelled",
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Prompt the parent session to synthesize its fan-out results.
 *
 * Uses `chat.inject` with the `fleet-complete` label — the same path the
 * gateway's own plan-completion synthesis uses (`requestFleetPlanOperatorSynthesis`
 * in `src/gateway/operator-trust-loop.ts`), so the result lands in the parent
 * transcript as an ordinary persisted message. That persistence is what makes
 * the synthesis survive navigating away and back, which an ephemeral UI-only
 * marker could not.
 *
 * The injected text names the results it is based on, so a forced synthesis
 * over a partial fan-out says so in the transcript rather than pretending the
 * run was complete.
 */
export async function requestFanInSynthesis(
  client: GatewayBrowserClient,
  opts: {
    parentSessionKey: string;
    /** Labels of the children whose results will be synthesized. */
    resultLabels?: string[];
    /** True when some children failed/cancelled and the operator forced this. */
    force?: boolean;
  },
): Promise<{ ok: true; messageId?: string } | { ok: false; error: string }> {
  const parentSessionKey = opts.parentSessionKey.trim();
  if (!parentSessionKey) {
    return { ok: false, error: "parentSessionKey required to synthesize" };
  }
  const labels = (opts.resultLabels ?? []).filter(Boolean);
  const scope = labels.length > 0 ? labels.join(", ") : "all completed subagents";
  const message = opts.force
    ? `[synthesis] Some subagents in this fan-out did not finish or were stopped. Synthesize a verdict from the ${scope} results that are available, and state explicitly which parts of the request are not covered.`
    : `[synthesis] All subagents in this fan-out have completed. Synthesize their results (${scope}) into a single verdict for me.`;
  try {
    const res = await client.request<{ messageId?: string }>("chat.inject", {
      sessionKey: parentSessionKey,
      message,
      label: "fleet-complete",
    });
    return { ok: true, messageId: res?.messageId };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
